import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ChevronDown, 
  Download, 
  Loader, 
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  MoreHorizontal,
  Filter,
  Pin,
  GripVertical
} from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const ROWS_PER_PAGE = 25;

const SmartSpreadsheetViewer = ({ documentUrl, documentId, user, onAskAboutData }) => {
  const [data, setData] = useState({ headers: [], rows: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queryInput, setQueryInput] = useState('');
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [currentSheet, setCurrentSheet] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [highlightedCells, setHighlightedCells] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [pinnedColumns, setPinnedColumns] = useState(new Set());
  const [resizing, setResizing] = useState(null);
  const [startX, setStartX] = useState(null);
  const [startWidth, setStartWidth] = useState(null);
  const tableRef = useRef(null);
  const [openMenuColumn, setOpenMenuColumn] = useState(null);

  useEffect(() => {
    fetchSpreadsheetData();
  }, [documentId, currentSheet]);

  const fetchSpreadsheetData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/spreadsheet/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        },
        params: {
          sheet_name: currentSheet
        }
      });

      const { data, metadata } = response.data;
      processSpreadsheetData(data);
      
      if (metadata.available_sheets?.length > 0) {
        setAvailableSheets(metadata.available_sheets);
        if (!currentSheet) {
          setCurrentSheet(metadata.available_sheets[0]);
        }
      }
    } catch (err) {
      console.error('Error loading spreadsheet:', err);
      setError(err.message || 'Failed to load spreadsheet data');
    } finally {
      setIsLoading(false);
    }
  };

  const processSpreadsheetData = (rawData) => {
    if (!rawData || !rawData.length) {
      setData({ headers: [], rows: [] });
      return;
    }

    const headers = Object.keys(rawData[0]);
    setData({
      headers,
      rows: rawData
    });
  };

  const handleQuery = async () => {
    if (!queryInput.trim()) return;

    try {
      const response = await axios.post(`${API_URL}/spreadsheet/${documentId}/modify`, {
        query: queryInput
      }, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        }
      });

      // Update the data with the response
      processSpreadsheetData(response.data);
      setQueryInput('');
    } catch (err) {
      console.error('Error processing query:', err);
      setError('Failed to process query');
    }
  };

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilter = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/spreadsheet/${documentId}/export`, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'exported_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data');
    }
  };

  // Column actions menu
  const renderColumnMenu = (column) => {
    if (!onAskAboutData) return null;

    return (
      <div className="relative">
        <button
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md
            text-gray-500 dark:text-gray-400"
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenuColumn(openMenuColumn === column ? null : column);
          }}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
        {openMenuColumn === column && (
          <>
            {/* Add overlay to catch clicks outside */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setOpenMenuColumn(null)}
            />
            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 
              rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-50">
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Column Actions
              </div>
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onAskAboutData(`Calculate average of ${column}`);
                  setOpenMenuColumn(null);
                }}
              >
                <span>Get Average</span>
              </button>
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onAskAboutData(`Show trend analysis for ${column}`);
                  setOpenMenuColumn(null);
                }}
              >
                <span>Show Trend</span>
              </button>
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onAskAboutData(`List unique values in ${column}`);
                  setOpenMenuColumn(null);
                }}
              >
                <span>Unique Values</span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderHeader = (column) => (
    <th 
      key={column}
      style={{ width: columnWidths[column] || 200 }}
      className="px-4 py-2 bg-gray-100 dark:bg-gray-800 
        group border-b border-gray-200 dark:border-gray-700 relative font-sans"
    >
      <div className="flex items-center justify-between space-x-2 h-8">
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <button
            onClick={(e) => {
              if (e?.stopPropagation) e.stopPropagation();
              toggleColumnPin(column);
            }}
            className={`flex-shrink-0 p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700
              ${pinnedColumns.has(column) ? 'text-blue-500' : 'text-gray-400'}`}
          >
            <Pin className="w-4 h-4" />
          </button>
          <span className="font-medium text-gray-700 dark:text-gray-200 truncate">
            {column}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {sortConfig.column === column && (
            <ChevronDown 
              className={`w-4 h-4 text-gray-500 dark:text-gray-400 transform transition-transform
                ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`}
            />
          )}
          {filters[column] && (
            <Filter className="w-4 h-4 text-blue-500" />
          )}
          {renderColumnMenu(column)}
        </div>
      </div>

      {/* Filter Input */}
      <div className="mt-2">
        <input
          type="text"
          placeholder="Filter..."
          className="w-full px-2 py-1 text-sm bg-white dark:bg-gray-700 
            border border-gray-300 dark:border-gray-600 rounded-md
            placeholder-gray-400 dark:placeholder-gray-500
            focus:outline-none focus:ring-1 focus:ring-blue-500"
          onClick={(e) => e?.stopPropagation?.()}
          onChange={(e) => handleFilter(column, e.target.value)}
        />
      </div>

      {/* Resize Handle */}
      <div
        className={`absolute right-0 top-0 bottom-0 w-2 cursor-col-resize 
          hover:bg-blue-500/50 
          ${resizing === column ? 'bg-blue-500/50' : 'opacity-0 group-hover:opacity-100'}`}
        onMouseDown={(e) => handleResizeStart(e, column)}
      >
        <div className="absolute inset-y-0 right-0 w-0.5 bg-blue-500" />
      </div>
    </th>
  );

  // Update the filtering logic to actually filter the rows
  const getFilteredRows = useCallback(() => {
    return data.rows.filter(row => {
      return Object.entries(filters).every(([column, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = String(row[column]).toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  }, [data.rows, filters]);

  // Update pagination related code
  const filteredRows = getFilteredRows();
  const totalPages = Math.ceil(filteredRows.length / ROWS_PER_PAGE);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    // Helper to get page numbers to show
    const getPageNumbers = () => {
      const pages = [];
      const totalPagesArray = [...Array(totalPages).keys()].map(n => n + 1);

      if (totalPages <= 7) {
        // If 7 or fewer pages, show all
        return totalPagesArray;
      }

      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        // Add ellipsis if current page is far from start
        pages.push('...');
      }

      // Add pages around current page
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        // Add ellipsis if current page is far from end
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }

      return pages;
    };

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
          <span>
            {((currentPage - 1) * ROWS_PER_PAGE) + 1}-{Math.min(currentPage * ROWS_PER_PAGE, filteredRows.length)} of {filteredRows.length}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1 rounded-md border border-gray-300 dark:border-gray-600 
              disabled:opacity-50 disabled:cursor-not-allowed
              hover:bg-gray-100 dark:hover:bg-gray-700
              text-gray-700 dark:text-gray-300"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          {getPageNumbers().map((page, idx) => (
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
            ) : (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`min-w-[32px] h-8 px-2 rounded-md text-sm
                  ${currentPage === page
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                {page}
              </button>
            )
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1 rounded-md border border-gray-300 dark:border-gray-600 
              disabled:opacity-50 disabled:cursor-not-allowed
              hover:bg-gray-100 dark:hover:bg-gray-700
              text-gray-700 dark:text-gray-300"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Worksheet selector
  const renderWorksheetSelector = () => (
    <div className="flex items-center space-x-2 px-4">
      <FileSpreadsheet className="w-5 h-5 text-gray-500 dark:text-gray-400" />
      <select
        value={currentSheet || ''}
        onChange={(e) => {
          const newSheet = e.target.value;
          setCurrentSheet(newSheet);
          setCurrentPage(1);
        }}
        className="form-select rounded-md border-gray-300 dark:border-gray-600 
          bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
          focus:ring-blue-500 focus:border-blue-500"
      >
        {availableSheets.map(sheet => (
          <option key={sheet} value={sheet}>{sheet}</option>
        ))}
      </select>
    </div>
  );

  // Cell selection handling
  const handleCellClick = (e, rowIndex, colIndex, value) => {
    if (!onAskAboutData) return;
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    const cellId = `${rowIndex}-${colIndex}`;
    setSelectedCells(prev => {
      if (prev.includes(cellId)) {
        return prev.filter(id => id !== cellId);
      }
      return [...prev, cellId];
    });
  };

  const handleAskAboutSelection = () => {
    if (selectedCells.length === 0 || !onAskAboutData) return;

    try {
      const selectedData = selectedCells.map(cellId => {
        const [rowIndex, colIndex] = cellId.split('-').map(Number);
        const column = data.headers[colIndex];
        const value = paginatedRows[rowIndex][column];
        return { column, value };
      });

      const query = `Tell me about these values: ${selectedData.map(d => 
        `${d.column}: ${d.value}`).join(', ')}`;
      onAskAboutData(query);
      
      // Clear selection after asking
      setSelectedCells([]);
    } catch (error) {
      console.error('Error processing selection:', error);
    }
  };

  // Column resize handlers
  const handleResizeStart = (e, column) => {
    e.preventDefault();
    e.stopPropagation();
    
    setResizing(column);
    setStartX(e.pageX);
    setStartWidth(columnWidths[column] || 200);
  };

  // Add useEffect for resize handling
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing || !startX || !startWidth) return;

      const diff = e.pageX - startX;
      const newWidth = Math.max(100, startWidth + diff);
      
      setColumnWidths(prev => ({
        ...prev,
        [resizing]: newWidth
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
      setStartX(null);
      setStartWidth(null);
    };

    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, startX, startWidth]);

  // Column pin handlers
  const toggleColumnPin = (column) => {
    setPinnedColumns(prev => {
      const next = new Set(prev);
      if (next.has(column)) {
        next.delete(column);
      } else {
        next.add(column);
      }
      return next;
    });
  };

  // Split columns into pinned and unpinned
  const getPinnedColumns = () => data.headers.filter(col => pinnedColumns.has(col));
  const getUnpinnedColumns = () => data.headers.filter(col => !pinnedColumns.has(col));

  // Update the table cell styles
  const renderCell = (row, column, rowIndex, colIndex, isPinned = false) => (
    <td 
      key={`${rowIndex}-${colIndex}`}
      onClick={(e) => handleCellClick(e, rowIndex, colIndex, row[column])}
      style={{ 
        width: columnWidths[column] || 200,
        minWidth: columnWidths[column] || 200,
        maxWidth: columnWidths[column] || 200
      }}
      className={`
        px-4 py-2 text-sm font-sans
        ${isPinned ? 'bg-white dark:bg-gray-900' : ''}
        border-b border-gray-200 dark:border-gray-700
        text-gray-900 dark:text-gray-100
        hover:bg-gray-50 dark:hover:bg-gray-800
        transition-colors duration-150
        overflow-hidden
        ${selectedCells.includes(`${rowIndex}-${colIndex}`) ? 'bg-blue-50 dark:bg-blue-900/50' : ''}
        ${highlightedCells.includes(`${rowIndex}-${colIndex}`) ? 'bg-yellow-50 dark:bg-yellow-900/50' : ''}
      `}
    >
      <div className="truncate">
        {row[column]}
      </div>
    </td>
  );

  // Add click handler to close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuColumn && !event.target.closest('.column-menu')) {
        setOpenMenuColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuColumn]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-500">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-6 bg-white/80 dark:bg-gray-900/80 
        backdrop-blur-xl border-b border-gray-200/10 dark:border-gray-700/20">
        {renderWorksheetSelector()}
        <div className="flex items-center space-x-2">
          {onAskAboutData && selectedCells.length > 0 && (
            <button
              onClick={handleAskAboutSelection}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 
                text-white rounded-full shadow-lg hover:shadow-xl
                transition-all duration-300 transform hover:scale-105"
            >
              Ask About Selection ({selectedCells.length})
            </button>
          )}
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 
              transition-colors duration-200 flex items-center shadow-sm whitespace-nowrap"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Improved table container */}
      <div className="flex-1 overflow-auto p-6">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
              {data.headers.map(column => renderHeader(column))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200/10 dark:divide-gray-700/20">
            {paginatedRows.map((row, rowIndex) => (
              <tr 
                key={rowIndex}
                className="group hover:bg-gray-50/50 dark:hover:bg-gray-800/50 
                  transition-colors duration-150"
              >
                {data.headers.map((column, colIndex) => (
                  <td 
                    key={`${rowIndex}-${colIndex}`}
                    onClick={(e) => handleCellClick(e, rowIndex, colIndex, row[column])}
                    className={cn(
                      "px-4 py-2 text-sm font-sans border-b border-gray-200/10 dark:border-gray-700/20",
                      "text-gray-900 dark:text-gray-100",
                      "transition-colors duration-150",
                      selectedCells.includes(`${rowIndex}-${colIndex}`) && 
                        'bg-blue-500/10 dark:bg-blue-500/20',
                      highlightedCells.includes(`${rowIndex}-${colIndex}`) && 
                        'bg-yellow-500/10 dark:bg-yellow-500/20'
                    )}
                  >
                    <div className="truncate">
                      {row[column]}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default SmartSpreadsheetViewer; 